import { useState, useEffect, useRef } from 'react';
import IdleTimer from 'react-idle-timer';
import { toastr } from 'react-redux-toastr';
import TimeService from '../../../services/timeService';
import './LogoutWarning.scss';

import AuthService from '../../../services/auth';

const oneMinute = 1000 * 60;
const refreshTokenTimer = Number(process.env.REACT_APP_REFRESH_RATE) || 10 * oneMinute;
const timeBeforeIdle = Number(process.env.REACT_APP_TIME_BEFORE_IDLE) || 5 * oneMinute;
const logoutNotificationTimer = Number(process.env.REACT_APP_LOGOUT_NOTIFICATION_TIMER) || 15 * oneMinute;

export default function LogoutWarning() {
    /**
     * @typedef {{
     *  logoutNotifTimer: NodeJS.Timeout | null,
     * }} RefreshOptions
     *
     * @type {[ refreshOptions: RefreshOptions, setRefreshOptions: Function]}
     */
    const [refreshOptions, setRefreshOptions] = useState({
        logoutNotifTimer: null,
    });
    const logoutNotifVisible = useRef(false);
    const lastRefresh = useRef(Date.now());

    const handleLogoutNotification = () => {
        const toastExpiration = Date.now() + logoutNotificationTimer;
        const logoutTimeout = setTimeout(() => { AuthService.logout(); }, logoutNotificationTimer);
        const Content = () => {
            const [now, setNow] = useState(Date.now());

            useEffect(() => {
                const interval = setInterval(() => {
                    setNow(Date.now());
                }, 1000);

                return () => {
                    // Clear interval when component unmount
                    clearInterval(interval);
                };
            }, []);

            return (
                <div className="logout-warning-content" >
                    <div className="logout-warning-text">
                        {`Your session will expire in ${TimeService.formatSecondsInMinSec(toastExpiration - now)}.`}
                    </div>
                </div>
            );
        };

        const toastrConfirmOptions = {
            id: 'logout-notif-toaster',
            okText: 'Stay logged in',
            onOk: () => {
                AuthService.refreshToken();

                if (refreshOptions.logoutNotifTimer) {
                    clearTimeout(refreshOptions.logoutNotifTimer);
                }

                if (logoutTimeout) {
                    clearTimeout(logoutTimeout);
                }

                logoutNotifVisible.current = false;
                lastRefresh.current = Date.now();
                setRefreshOptions({
                    logoutNotifTimer: null,
                });
            },
            component: Content,
            disableCancel: true,
            position: 'top-center',
        };
        toastr.confirm('Logout notification', toastrConfirmOptions);
    };

    const handleOnAction = (/** @type {Event} */ event) => {
        if ((Date.now() - lastRefresh.current) > refreshTokenTimer) {
            AuthService.refreshToken();
            if (refreshOptions.logoutNotifTimer) {
                clearTimeout(refreshOptions.logoutNotifTimer);
            }
            setRefreshOptions({
                ...refreshOptions,
                logoutNotifTimer: null,
            });
            lastRefresh.current = Date.now();
        }
    };

    const handleOnActive = (/** @type {Event} */ event) => {
        if (refreshOptions.logoutNotifTimer) {
            clearTimeout(refreshOptions.logoutNotifTimer);
        }

        setRefreshOptions({
            ...refreshOptions,
            logoutNotifTimer: null,
        });
    };

    const handleOnIdle = (/** @type {Event} */ event) => {
        if (!logoutNotifVisible.current) {
            logoutNotifVisible.current = true;
            setRefreshOptions({
                ...refreshOptions,
                logoutNotifTimer: handleLogoutNotification(),
            });
        }
    };

    return (
        <IdleTimer
            timeout={timeBeforeIdle}
            onActive={handleOnActive}
            onIdle={handleOnIdle}
            onAction={handleOnAction}
            debounce={1000}
        />
    );
}
